/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable import/no-default-export */
/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import React from 'react';
import { Column, useSortBy, useTable, useFlexLayout } from 'react-table';
import { Box, Center, Spinner, Text } from '@chakra-ui/core';
import { useTranslation } from 'react-i18next';
import { FixedSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { Position, PositionSort, SortOrder } from '../types';
import { PositionTableRow } from './PositionTableRow';
import { PostionTableHeader } from './PostionTableHeader';
import { useStoreActions, useStoreState } from '../../../../../models/hooks';
import { PositionPagination } from './PositionPagination';

export default function PositionTable() {
  const { t } = useTranslation('moniterByRecruiters');

  const { isBulkAssignMode, positions, isFetchingPosition, positionFilters } = useStoreState(
    /* istanbul ignore next */ (state) => state.moniterByRecruiters,
  );
  const { setPositionFilter } = useStoreActions(/* istanbul ignore next */ (state) => state.moniterByRecruiters);

  const columns: Column<Position>[] = React.useMemo(() => {
    const data: Column<Position>[] = [
      { accessor: 'id', id: 'checkbox', width: 20, disableSortBy: true },
      {
        Header: t('positions.bulk.table.location', 'Location') as string,
        accessor: 'businessName',
        id: 'businessName',
        disableSortBy: true,
      },
      {
        Header: t('positions.bulk.table.position', 'Position') as string,
        accessor: 'customName',
        id: 'positionName',
        disableSortBy: true,
      },
      {
        Header: t('positions.tableHeader.location') ?? /* istanbul ignore next */ 'Location & Position',
        accessor: 'customName',
        id: PositionSort.Location,
        disableSortBy: isBulkAssignMode,
      },
      {
        Header: t('positions.tableHeader.province') ?? /* istanbul ignore next */ 'Province',
        accessor: 'state',
        id: 'province',
        width: 80,
        disableSortBy: true,
      },
      {
        Header: t('positions.tableHeader.city') ?? /* istanbul ignore next */ 'City',
        accessor: 'city',
        id: 'city',
        maxWidth: 120,
        disableSortBy: true,
      },
      {
        Header: t('positions.tableHeader.following') ?? /* istanbul ignore next */ 'Following',
        id: PositionSort.Followed,
        width: 60,
        accessor: 'assignedRecruitersCount',
        disableSortBy: isBulkAssignMode,
      },
      {
        Header: t('positions.tableHeader.watching') ?? /* istanbul ignore next */ 'Watching',
        id: PositionSort.Watched,
        width: 60,
        accessor: 'watchedRecruitersCount',
        disableSortBy: isBulkAssignMode,
      },
      {
        Header: t('positions.tableHeader.none') ?? /* istanbul ignore next */ 'None',
        id: 'none',
        width: 40,
        disableSortBy: true,
      },
      // { Header: 'Action', id: 'action', disableSortBy: true, width: 100 },
    ];

    return data;
  }, [t, isBulkAssignMode]);

  const data = React.useMemo(() => positions, [positions]);

  const {
    getTableProps,
    getTableBodyProps,
    rows,
    prepareRow,
    headerGroups,
    state: { sortBy },
    setSortBy,
    setHiddenColumns,
  } = useTable(
    {
      data,
      columns,
      manualSortBy: !isBulkAssignMode,
      disableMultiSort: true,
      autoResetHiddenColumns: false,
      initialState: {
        sortBy: isBulkAssignMode
          ? []
          : [
              {
                id: positionFilters.sortBy ?? PositionSort.Location,
                desc: positionFilters.sortOrder === SortOrder.desc,
              },
            ],
      },
    },
    useSortBy,
    useFlexLayout,
  );

  React.useLayoutEffect(() => {
    setHiddenColumns(
      isBulkAssignMode ? ['location', 'followed', 'watched', 'none'] : ['checkbox', 'businessName', 'positionName'],
    );
  }, [isBulkAssignMode, setHiddenColumns]);

  React.useEffect(() => {
    /* istanbul ignore next */
    if (sortBy.length === 0) {
      if (positionFilters.sortBy !== PositionSort.Location || positionFilters.sortOrder !== SortOrder.asc) {
        setPositionFilter({ key: 'sortBy', value: PositionSort.Location });
        setPositionFilter({ key: 'sortOrder', value: SortOrder.asc });
        setPositionFilter({ key: 'page', value: 1 });
        setSortBy([
          {
            id: PositionSort.Location,
            desc: false,
          },
        ]);
      }
      return;
    }

    const [sort] = sortBy;
    /* istanbul ignore next */
    if (
      positionFilters.sortBy !== sort.id ||
      positionFilters.sortOrder !== (sort.desc ? /* istanbul ignore next */ SortOrder.desc : SortOrder.asc)
    ) {
      setPositionFilter({ key: 'sortBy', value: sort.id as PositionSort });
      setPositionFilter({ key: 'sortOrder', value: sort.desc ? /* istanbul ignore next */ SortOrder.desc : SortOrder.asc });
      setPositionFilter({ key: 'page', value: 1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, positionFilters.sortBy, positionFilters.sortOrder, setPositionFilter]);

  const RenderRow = React.useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const row = rows[index];
      prepareRow(row);
      return <PositionTableRow row={row} style={style} />;
    },
    [prepareRow, rows],
  );

  let content;

  if (isFetchingPosition) {
    content = (
      <Center py={5} data-testid="recruiter-table-loading">
        <Spinner size="md" colorScheme="blue" />
      </Center>
    );
  } else if (rows.length > 0) {
    const rowHeight = isBulkAssignMode ? 30 : 45;
    content = (
      <AutoSizer>
        {({ height, width }: { height: number; width: number }) => (
          <FixedSizeList height={height} itemCount={rows.length} itemSize={rowHeight} width={width}>
            {RenderRow}
          </FixedSizeList>
        )}
      </AutoSizer>
    );
  } else {
    content = (
      <Center py={5}>
        <Text>{t('positions.emptyData')}</Text>
      </Center>
    );
  }

  return (
    <Box
      sx={{
        border: '1px solid #e5e5e5',
        borderRadius: '8px',
        height: `calc(100vh - 255px ${isBulkAssignMode ? '- 50px' : ' - 0px'})`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box {...getTableProps()} flex={1} display="flex" flexDirection="column">
        <PostionTableHeader headerGroups={headerGroups} sortBy={sortBy} />
        <Box {...getTableBodyProps()} flex={1}>
          {content}
        </Box>
      </Box>
      <PositionPagination />
    </Box>
  );
}
